/**
 * Overview group-card HTML builders (FE-5b) — extracted from overview.route.js.
 */

import { DEFAULT_REMOTE_SIREN_MS } from "./constants.js";
import {
  canEditSharedGroupAlarmSettings,
  canGroupSirenForSlot,
  groupDeviceIdsFromSlot,
  groupSharedBySlot,
  humanizeGroupAlarmLine,
  isOverviewDeviceOnline,
  overviewSlotDeviceSampleIncomplete,
  overviewSlotLoadedDeviceCount,
  overviewSlotServerTotal,
  shareScopeBadgesHtml,
} from "./overview-group-slot-helpers.js";
import { resolveOverviewGroupCardTitle } from "./overview-group-slots.js";

/**
 * @param {object} d
 * @param {{ checked?: boolean, disabled?: boolean, contextKey?: string, lockInGroup?: boolean, foreignOccupied?: boolean, onCardRemovable?: boolean }} [opts]
 * @param {{ canonicalGroupKey: (v: unknown) => string, displayGroupName: (v: unknown) => string, escapeHtml: (s: string) => string }} keys
 * @returns {string}
 */
export function buildOverviewGroupDeviceRowHtml(d, opts, keys) {
  const {
    checked = false,
    disabled = false,
    contextKey = "",
    lockInGroup = false,
    foreignOccupied = false,
    onCardRemovable = false,
  } = opts || {};
  const did = String(d && d.device_id != null ? d.device_id : "").trim();
  if (!did) return "";
  const name0 = d && d.display_label != null && String(d.display_label).trim();
  const name = name0 || did;
  const ck = checked ? " checked" : "";
  const di = disabled ? " disabled" : "";
  const lockCls = lockInGroup ? " grp-device-row--locked" : "";
  const blockCls = foreignOccupied ? " grp-device-row--blocked" : "";
  const ctx = keys.canonicalGroupKey(contextKey || "");
  const ngRaw = d && d.notification_group != null ? String(d.notification_group).trim() : "";
  const ngCanon = keys.canonicalGroupKey(ngRaw);
  let rowMod = " grp-device-row--neutral";
  let statusHtml = '<span class="grp-device-pill grp-device-pill--neutral">—</span>';
  if (ngCanon) {
    if (ctx && ngCanon === ctx) {
      rowMod = " grp-device-row--this";
      const thisTitle = onCardRemovable
        ? "Member — uncheck and press Save to remove from this group"
        : "Already on this card";
      statusHtml =
        `<span class="grp-device-pill grp-device-pill--this" title="${keys.escapeHtml(thisTitle)}">On card</span>` +
        (lockInGroup ? ' <span class="grp-device-lock-tag" title="Uncheck disabled — remove via board profile if needed">Locked</span>' : "");
    } else {
      rowMod = " grp-device-row--other";
      const dn = keys.displayGroupName(ngRaw);
      const short = dn.length > 26 ? `${dn.slice(0, 26)}…` : dn;
      statusHtml = `<span class="grp-device-pill grp-device-pill--other" title="${keys.escapeHtml(ngRaw)}">${keys.escapeHtml(short)}</span>`;
      if (foreignOccupied) {
        statusHtml +=
          ` <span class="grp-device-lock-tag" title="In group ${keys.escapeHtml(ngRaw)} — clear on board profile to move">Other</span>`;
      }
    }
  }
  const blockTitle = foreignOccupied && ngRaw
    ? `In group ${ngRaw} — clear on board profile before adding here`
    : "";
  const labelTitleAttr =
    foreignOccupied && !lockInGroup && blockTitle ? ` title="${keys.escapeHtml(blockTitle)}"` : lockInGroup ? ` title="Remove from this group via board profile if you need to move it."` : "";
  const nameTitle = name0 ? ` title="${keys.escapeHtml(did)}"` : "";
  const idLine = name0
    ? ""
    : `<span class="grp-device-row__id mono" title="Board ID">${keys.escapeHtml(did)}</span>`;
  return (
    `<label class="grp-device-row${rowMod}${lockCls}${blockCls}"${labelTitleAttr}>` +
    `<input type="checkbox" class="grp-pick-chk grp-device-row__chk" value="${keys.escapeHtml(did)}"${ck}${di} />` +
    `<div class="grp-device-row__main">` +
    `<span class="grp-device-row__name"${nameTitle}>${keys.escapeHtml(name)}</span>` +
    idLine +
    `</div>` +
    `<div class="grp-device-row__status">${statusHtml}</div>` +
    `</label>`
  );
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey: string }} slot
 * @param {object} ctx
 * @returns {string}
 */
export function buildOverviewGroupCardHtml(slot, ctx) {
  const {
    meta,
    groupSettingsMap,
    selectedGroup,
    me,
    canFn,
    escapeHtml,
    devices,
    byId,
    canonicalGroupKey,
    displayGroupName,
    defaultSirenMs = DEFAULT_REMOTE_SIREN_MS,
  } = ctx;
  const slotCtx = { me, canonicalGroupKey };
  const g = slot.groupKey;
  let total;
  let on;
  let off;
  let rows;
  if (slot.serverCounts && Number.isFinite(Number(slot.serverCounts.total))) {
    total = Number(slot.serverCounts.total);
    on = Number(slot.serverCounts.online != null ? slot.serverCounts.online : 0);
    off = Number(slot.serverCounts.offline != null ? slot.serverCounts.offline : Math.max(0, total - on));
    rows = groupDeviceIdsFromSlot(slot, devices, byId, slotCtx)
      .map((id) => byId.get(String(id)))
      .filter(Boolean);
  } else {
    const ids = groupDeviceIdsFromSlot(slot, devices, byId, slotCtx);
    rows = ids.map((id) => byId.get(String(id))).filter(Boolean);
    total = rows.length;
    on = rows.filter((d) => isOverviewDeviceOnline(d)).length;
    off = Math.max(0, total - on);
  }
  const m = (meta && meta[slot.metaKey]) || {};
  const gs = (groupSettingsMap && groupSettingsMap.get(slot.metaKey)) || {
    trigger_mode: "continuous",
    trigger_duration_ms: defaultSirenMs,
    delay_seconds: 0,
    reboot_self_check: false,
  };
  const meName = String((me && me.username) || "").trim();
  const foreignOwnerSlot = !!(slot.tenantOwner && meName && slot.tenantOwner !== meName);
  const isSharedGroup = foreignOwnerSlot || groupSharedBySlot(slot, devices, byId, slotCtx).length > 0;
  const scopeShareHtml = shareScopeBadgesHtml(rows, escapeHtml);
  const alarmHumanLine = humanizeGroupAlarmLine(gs, defaultSirenMs);
  const anyGranteeDeviceOnCard = foreignOwnerSlot || rows.some((d) => d && d.is_shared);
  const canSettings = !isSharedGroup || canEditSharedGroupAlarmSettings(slot, devices, byId, slotCtx);
  const settingsTitle = !canSettings
    ? "Partial share: need Operate on every board on this card to edit alarm settings"
    : (isSharedGroup
      ? "Edits sync to the owning admin’s group settings"
      : "");
  const sampleIncomplete = overviewSlotDeviceSampleIncomplete(slot, devices, byId, slotCtx);
  const loadedSample = overviewSlotLoadedDeviceCount(slot, devices, byId, slotCtx);
  const serverTotal = overviewSlotServerTotal(slot);
  const clientOn = rows.filter((d) => isOverviewDeviceOnline(d)).length;
  const serverOn = slot.serverCounts && Number.isFinite(Number(slot.serverCounts.online))
    ? Number(slot.serverCounts.online)
    : null;
  const presenceStale = serverOn === 0 && clientOn > 0 && total > 0;
  const sampleBadge = sampleIncomplete && serverTotal != null
    ? `<span class="badge partial" title="Showing a loaded sample; full group counts and alarms use the complete roster.">sample ${loadedSample}/${serverTotal}</span>`
    : "";
  const presenceStaleBadge = presenceStale
    ? `<span class="badge offline" title="Online status looks out of date — refresh the page or wait a moment.">status lag</span>`
    : "";
  const sirenOk = canGroupSirenForSlot(slot, devices, byId, canFn, slotCtx);
  const sirenTitle = sirenOk ? "" : "Need Alert access or Operate on at least one board in this group";
  const shareBtn = me && (me.role === "superadmin" || (me.role === "admin" && canFn("can_manage_users"))) && !anyGranteeDeviceOnCard
    ? `<button class="group-del-ico js-share-group" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" title="Share this notification group with other users">⇪</button>`
    : "";
  const ownerTag = String(slot.tenantOwner || "").trim();
  const hasCorner = !!(ownerTag || shareBtn);
  const ownerPill = ownerTag
    ? `<span class="card-owner-tag" title="Account owner">${escapeHtml(ownerTag)}</span>`
    : "";
  const cornerHtml = hasCorner
    ? `<div class="device-card__corner-tr" role="group" aria-label="Account">${ownerPill}${shareBtn}</div>`
    : "";
  const cardTitle = resolveOverviewGroupCardTitle(slot, m, displayGroupName);
  const subKey = canonicalGroupKey(cardTitle) === canonicalGroupKey(g) ? "" : g;
  return `<article class="device-card js-group-card ${hasCorner ? "js-group-card--has-corner " : ""}${selectedGroup === slot.metaKey ? "is-selected" : ""}" data-meta-key="${escapeHtml(slot.metaKey)}" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" style="cursor:pointer;position:relative">
      ${cornerHtml}
      <h3><div class="device-primary-name">${escapeHtml(cardTitle)}</div>${subKey ? `<div class="device-id-sub mono">${escapeHtml(subKey)}</div>` : ""}</h3>
      <div class="meta" style="margin-bottom:8px">${escapeHtml(alarmHumanLine)}</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px;align-items:center">
        <span class="badge neutral">total ${total}</span>
        <span class="badge online">online ${on}</span>
        <span class="badge offline">offline ${off}</span>
        ${scopeShareHtml}
        ${sampleBadge}
        ${presenceStaleBadge}
      </div>
      <div class="meta">Owner: ${escapeHtml(m.owner_name || "—")} · ${escapeHtml(m.phone || "—")} · ${escapeHtml(m.email || "—")}</div>
      <div class="group-card-actions">
        <div class="group-card-actions__alarms">
          <button class="btn sm danger js-alert-on" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" ${sirenOk ? "" : "disabled"} title="${escapeHtml(sirenTitle)}">Alarm ON</button>
          <button class="btn sm secondary js-alert-off" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" ${sirenOk ? "" : "disabled"} title="${escapeHtml(sirenTitle)}">Alarm OFF</button>
        </div>
        <div class="group-card-actions__manage">
          <button class="btn sm secondary js-group-settings" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" ${canSettings ? "" : "disabled"} title="${escapeHtml(settingsTitle)}">Settings</button>
          <button class="btn sm secondary js-edit-group" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" ${isSharedGroup ? "disabled title=\"Shared group: board membership is read-only\"" : ""}>Edit</button>
          <button class="btn sm danger js-del-group" data-group="${escapeHtml(g)}" data-owner="${escapeHtml(slot.tenantOwner)}" data-meta-key="${escapeHtml(slot.metaKey)}" type="button" ${isSharedGroup ? "disabled title=\"Shared group cannot be deleted\"" : "title=\"Delete group\""}>Delete</button>
        </div>
      </div>
    </article>`;
}
